import {
  ClickAwayListener,
  debounce,
  IconButton,
  IconButtonProps,
  InputAdornment,
  Stack,
  TextFieldProps,
} from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import {
  FormTextField,
  FormTextFieldStyledProps,
  InputEndAdornmentProps,
} from '@schooly/components/form-text-field';
import { useFlag } from '@schooly/hooks/use-flag';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CrossIcon, SearchIcon } from '../../assets/assets';

type SearchProps = {
  value: string;
  /*
       onChange_MemoizedCallbackOnly – this is special notification for developers
       to use only memoized callbacks, cuz updating state is tied to this property update.
       Infinite state updates otherwise
  */
  onChange_MemoizedCallbackOnly: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  withDebounce?: boolean;
  iconProps?: IconButtonProps;
};

type ExpandableSearchProps = SearchProps &
  Omit<TextFieldProps, 'onChange'> &
  FormTextFieldStyledProps &
  InputEndAdornmentProps;

export const ExpandableSearch: FC<ExpandableSearchProps> = ({
  isDisabled,
  isLoading,
  value: initialValue,
  onChange_MemoizedCallbackOnly: onChange,
  withDebounce,
  iconProps,
  sx,
  ...rest
}) => {
  const [opened, open, close] = useFlag(!!initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(initialValue);
  const isFocused = useRef(false);
  const shouldBeFocused = useRef(false);
  const onChangeDebounce = useMemo(
    () =>
      debounce(
        (val: string) => {
          onChange(val);
        },
        withDebounce ? 500 : 0,
      ),
    [withDebounce, onChange],
  );

  useEffect(() => {
    onChangeDebounce(value);
  }, [value, onChangeDebounce]);

  useEffect(() => {
    setValue(initialValue);
    if (initialValue) {
      open();
      return;
    }

    if (!isFocused.current) {
      close();
    }
  }, [initialValue, open, close]);

  const handleClickAway = useCallback(() => {
    if (!value) close();
  }, [close, value]);

  const handleClear = useCallback(() => {
    setValue('');
    inputRef.current?.focus();
  }, []);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Stack
        data-test-id={'small-search'}
        sx={{
          position: 'relative',
          height: 40,
          direction: 'row',
          justifyContent: 'center',
        }}
      >
        {opened ? (
          <FormTextField
            inputRef={inputRef}
            disabled={isLoading || isDisabled}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            hideLabel
            solid
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton disabled={isLoading || isDisabled} inverse onClick={handleClear}>
                    <CrossIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus={shouldBeFocused.current}
            showEndAdornment={true}
            sx={(theme) => ({
              maxWidth: 180,
              '&& .MuiInputBase-input': {
                padding: theme.spacing(1, 1.25),
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: 130,
              },
              '&&& .MuiInputAdornment-root': {
                mt: 0,
              },
              ...((typeof sx === 'function' ? sx(theme) : sx) as SystemStyleObject),
            })}
            {...rest}
            onBlur={(e) => {
              isFocused.current = false;
              rest.onBlur?.(e);
            }}
            onFocus={(e) => {
              shouldBeFocused.current = false;
              isFocused.current = true;
              rest.onFocus?.(e);
            }}
          />
        ) : (
          <IconButton
            sx={{
              color: 'text.primary',
            }}
            inverse
            onClick={() => {
              shouldBeFocused.current = true;
              open();
            }}
            {...iconProps}
          >
            <SearchIcon />
          </IconButton>
        )}
      </Stack>
    </ClickAwayListener>
  );
};
